import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const reveal = (delay = 0) => ({
  initial: { y: 48, opacity: 0 },
  whileInView: { y: 0, opacity: 1 },
  viewport: { once: true, margin: "-10% 0px" },
  transition: { duration: 0.9, ease: EASE, delay },
});

const PLUMES = [14, 36, 60, 84];

export default function VentFooter({ onNavigate }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received — we'll resurface with answers soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Couldn't reach the surface. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <footer
      id="contact"
      data-testid="vent-footer"
      className="relative flex min-h-[125vh] flex-col justify-end overflow-hidden px-6 pb-10 pt-48 text-[#F8FAFC] md:px-12"
    >
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[75vh]"
        style={{
          background:
            "radial-gradient(ellipse 90% 70% at 50% 112%, rgba(245,130,32,0.62), rgba(245,130,32,0.18) 45%, transparent 72%)",
        }}
        aria-hidden="true"
      />

      {PLUMES.map((x, i) => (
        <motion.span
          key={x}
          className="pointer-events-none absolute bottom-[12vh] block h-56 w-14 -translate-x-1/2 rounded-full blur-2xl"
          style={{
            left: `${x}%`,
            background:
              "linear-gradient(to top, rgba(255,180,95,0.85), rgba(255,150,60,0.18) 70%, transparent)",
          }}
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: [-20, -170], opacity: [0, 0.9, 0] }}
          transition={{
            duration: 7 + i * 1.4,
            delay: i * 1.6,
            repeat: Infinity,
            ease: "linear",
            times: [0, 0.4, 1],
          }}
          aria-hidden="true"
        />
      ))}

      <svg
        className="pointer-events-none absolute bottom-0 left-0 h-[16vh] w-full md:h-[22vh]"
        viewBox="0 0 1440 220"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path
          d="M0 220 V168 L60 158 L95 108 L130 163 L200 148 L235 68 L270 148 L340 138 L375 93 L410 143 L500 128 L545 38 L590 133 L680 118 L715 78 L750 123 L840 113 L885 53 L930 118 L1020 103 L1055 63 L1090 108 L1180 98 L1225 43 L1270 103 L1360 93 L1440 83 V220 Z"
          fill="#01070f"
        />
      </svg>

      <div className="relative z-10">
        <motion.p
          {...reveal(0)}
          className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#f58220] md:text-[11px]"
          data-testid="vent-overline"
        >
          3000m — The Seafloor
        </motion.p>
        <h2 className="mt-8 font-display font-light tracking-tighter">
          <motion.span
            {...reveal(0.08)}
            className="block text-5xl leading-[1.02] md:text-7xl"
          >
            The descent ends.
          </motion.span>
          <motion.span
            {...reveal(0.16)}
            className="mt-2 block font-editorial text-5xl italic leading-[1.05] text-[#f58220] md:text-7xl"
          >
            The work begins.
          </motion.span>
        </h2>
        <motion.p
          {...reveal(0.24)}
          className="mt-8 max-w-lg text-sm leading-relaxed text-[#F8FAFC]/65 md:text-base"
        >
          Field surveys, numerical modelling, environmental assessment and
          structures design — delivered across the Middle East, India and
          Vietnam.
        </motion.p>

        <motion.form
          {...reveal(0.32)}
          onSubmit={sendMessage}
          className="mt-16 max-w-2xl"
          data-testid="contact-form"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#F8FAFC]/60">
            Contact us
          </p>
          <p
            className="mt-4 font-editorial text-3xl italic leading-tight text-[#F8FAFC] md:text-4xl"
            data-testid="contact-form-title"
          >
            Have a coastline that needs “answers”?
          </p>
          <div className="mt-8 grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Name"
              data-testid="contact-name-input"
              className="w-full border-b border-white/25 bg-transparent py-3 font-mono text-sm text-[#F8FAFC] outline-none transition-colors duration-300 placeholder:text-white/25 focus:border-[#f58220]"
            />
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="Email"
              data-testid="contact-email-input"
              className="w-full border-b border-white/25 bg-transparent py-3 font-mono text-sm text-[#F8FAFC] outline-none transition-colors duration-300 placeholder:text-white/25 focus:border-[#f58220]"
            />
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="Phone"
              data-testid="contact-phone-input"
              className="w-full border-b border-white/25 bg-transparent py-3 font-mono text-sm text-[#F8FAFC] outline-none transition-colors duration-300 placeholder:text-white/25 focus:border-[#f58220]"
            />
            <textarea
              required
              rows={3}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Tell us about your coastline"
              data-testid="contact-message-input"
              className="w-full resize-none border-b border-white/25 bg-transparent py-3 font-mono text-sm text-[#F8FAFC] outline-none transition-colors duration-300 placeholder:text-white/25 focus:border-[#f58220] sm:col-span-2"
            />
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit-button"
            className="mt-8 rounded-full bg-[#f58220] px-8 py-3.5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#1a0d00] transition-[transform,background-color] duration-300 hover:scale-105 hover:bg-[#ff9440] active:scale-95 disabled:opacity-60"
          >
            {sending ? "Sending…" : "Send message"}
          </button>
        </motion.form>

        <motion.div
          {...reveal(0.1)}
          className="mt-20 flex items-end gap-5 md:gap-8"
          data-testid="footer-wordmark"
        >
          <img
            src="/okeanos-mark.png"
            alt="Okeanos wave mark"
            className="w-[clamp(3rem,7vw,6.5rem)] shrink-0"
            style={{ filter: "drop-shadow(0 0 22px rgba(245,130,32,0.35))" }}
          />
          <span
            className="font-brand text-[clamp(3.4rem,11vw,10rem)] leading-none text-[#F8FAFC]"
            style={{ textShadow: "0 0 30px rgba(38,198,211,0.35)" }}
          >
            Oκεαηοs
          </span>
        </motion.div>

        <motion.div
          {...reveal(0.15)}
          className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-3"
          data-testid="contact-addresses"
        >
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#f58220]">
              India
            </p>
            <p className="mt-2 text-[11px] leading-relaxed text-[#F8FAFC]/50">
              No 6, Mallika Nagar (Extn), West Tambaram, Chennai 600045, Tamil
              Nadu
            </p>
          </div>
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#f58220]">
              UAE
            </p>
            <p className="mt-2 text-[11px] leading-relaxed text-[#F8FAFC]/50">
              Okeanos Engineering Consultants LLC FZ, Meydan Grandstand, 6th
              floor, Nad Al Sheba, Dubai
            </p>
          </div>
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#f58220]">
              Vietnam
            </p>
            <p className="mt-2 text-[11px] leading-relaxed text-[#F8FAFC]/50">
              #808, Building CT1-1, Me Tri Ha Urban Zone, Tu Liem District,
              Hanoi · +84 983 642 589
            </p>
          </div>
        </motion.div>

        <div className="mt-12 flex flex-wrap items-end justify-between gap-8 border-t border-white/10 pt-8">
          <span className="flex items-center gap-2.5">
            <img src="/okeanos-mark.png" alt="Okeanos" className="h-6 w-auto" />
            <span className="font-brand text-lg leading-none">Oκεαηοs</span>
          </span>
          <div className="flex flex-col gap-2">
            <a
              href="mailto:Info@okeanos.in"
              data-testid="contact-email-link"
              className="group inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] text-[#F8FAFC]/80 transition-colors duration-300 hover:text-[#f58220]"
            >
              INFO@OKEANOS.IN
              <ArrowUpRight
                size={14}
                strokeWidth={1.5}
                className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              />
            </a>
            <a
              href="mailto:Okeanosindia@gmail.com"
              data-testid="contact-email-link-2"
              className="group inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] text-[#F8FAFC]/80 transition-colors duration-300 hover:text-[#f58220]"
            >
              OKEANOSINDIA@GMAIL.COM
              <ArrowUpRight
                size={14}
                strokeWidth={1.5}
                className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              />
            </a>
          </div>
          <button
            type="button"
            onClick={() => onNavigate(0)}
            data-testid="resurface-button"
            className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#F8FAFC]/50 transition-colors duration-300 hover:text-[#F8FAFC]"
          >
            Resurface ↑
          </button>
          <p className="w-full font-mono text-[9px] tracking-[0.2em] text-[#F8FAFC]/35 md:w-auto">
            © 2026 Okeanos Coastal Engineering (P) Ltd.
          </p>
        </div>
      </div>
    </footer>
  );
}
